import { useEffect, useRef } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { fetchExams } from '@/services/exams';
import { examsSelectors } from '@/slices/exams/examsSlice';
import { reloadLectureSelection } from '@/slices/lectures/lectureSelectionPersist';
import { flashcardRunInvalidated } from '@/slices/flashcardRun/flashcardRunSlice';
import { topicFiltersRefreshRequested } from '@/slices/todayTopics/todayTopicsSlice';
import type { ChatActionProposal } from '@/slices/chat/chatSlice';
import type { ChatRootState } from '@/slices/chat/chatSelectors';
import type { AppDispatch, RootState } from '@/store/store';
import { isPreclinicalExam } from '@/utils/examUtils';
import { useRefreshStudySessionAndContent } from '@/utils/refreshStudySession';

type RefreshableActionKind = 'plan_change' | 'study_change';

/**
 * Whether the action changed the Preclinical lecture selection. `unknown` is a
 * study change whose result does not name its op (an `already_executed`
 * confirm has no result), which could have.
 */
type LectureSelectionChange = 'yes' | 'no' | 'unknown';

interface ExecutedAction {
  refreshKey: string;
  kind: RefreshableActionKind;
  invalidatesFlashcardRun: boolean;
  lectureSelectionChange: LectureSelectionChange;
}

const ACTION_NAME_FALLBACKS: Record<string, RefreshableActionKind> = {
  propose_plan_change: 'plan_change',
  propose_study_change: 'study_change',
  save_outside_result: 'study_change',
};

const PLAN_FIELDS_THAT_CAN_CHANGE_TODAYS_CARDS = new Set([
  'workload_tier',
  'daily_new_cards',
  'study_parts',
  'today_questions',
  'today_cards',
  'today_videos',
  'skip_today',
  'primary_exam',
]);

const refreshKind = (
  proposal: ChatActionProposal
): RefreshableActionKind | null => {
  const kind = proposal.outcome?.result?.kind;
  if (kind === 'plan_change' || kind === 'study_change') return kind;

  // `already_executed` confirms the card without returning its original
  // result. The proposal name still identifies the state that became stale.
  if (proposal.outcome?.result === null) {
    return ACTION_NAME_FALLBACKS[proposal.name] ?? null;
  }

  return null;
};

const invalidatesFlashcardRun = (
  proposal: ChatActionProposal,
  kind: RefreshableActionKind
): boolean => {
  if (kind === 'study_change') return true;

  const result = proposal.outcome?.result;
  if (result?.kind !== 'plan_change') return true;

  const field = result.field;
  return typeof field !== 'string'
    ? true
    : PLAN_FIELDS_THAT_CAN_CHANGE_TODAYS_CARDS.has(field);
};

const lectureSelectionChange = (
  proposal: ChatActionProposal,
  kind: RefreshableActionKind
): LectureSelectionChange => {
  if (kind !== 'study_change') return 'no';

  const result = proposal.outcome?.result;
  const op = result?.kind === 'study_change' ? result.op : undefined;
  if (typeof op !== 'string') return 'unknown';
  return op === 'lectures' ? 'yes' : 'no';
};

const selectExecutedActions = createSelector(
  [(state: ChatRootState) => state.chat.turns],
  (turns): ExecutedAction[] =>
    Object.values(turns).flatMap((turn) => {
      const proposal = turn.actionProposal;
      if (!proposal || proposal.status === 'pending') return [];

      const kind = refreshKind(proposal);
      return kind
        ? [
            {
              // Undo is a second mutation of the same action. Keying only on
              // actionId would let the confirm refresh suppress its refresh.
              refreshKey: `${proposal.actionId}:${proposal.status}`,
              kind,
              invalidatesFlashcardRun: invalidatesFlashcardRun(proposal, kind),
              lectureSelectionChange: lectureSelectionChange(proposal, kind),
            },
          ]
        : [];
    })
);

/**
 * Refresh app state after a confirmed companion action changes study state.
 *
 * Both button confirms and typed confirms finish as confirmed proposals in the
 * chat slice, so observing that state covers `actionProposalConfirmed` and
 * `action.resolved` without coupling the stream client to dashboard services.
 */
export const useRefreshAfterCompanionAction = (): void => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const executedActions = useSelector(selectExecutedActions);
  const { refreshStudySessionAndContent } = useRefreshStudySessionAndContent();

  const refreshedActionIds = useRef(
    new Set(executedActions.map(({ refreshKey }) => refreshKey))
  );

  useEffect(() => {
    executedActions.forEach(
      ({ refreshKey, invalidatesFlashcardRun, lectureSelectionChange }) => {
        if (refreshedActionIds.current.has(refreshKey)) return;
        refreshedActionIds.current.add(refreshKey);

        if (invalidatesFlashcardRun) {
          // The session id usually survives a plan/study change, so invalidate
          // the route cache explicitly when the action can change today's cards.
          dispatch(flashcardRunInvalidated());
        }

        const mayHaveChangedLectures =
          lectureSelectionChange === 'yes' ||
          (lectureSelectionChange === 'unknown' &&
            isPreclinicalExam(
              examsSelectors.selectPrimaryExam(store.getState())
            ));

        if (mayHaveChangedLectures) {
          // Started now, not after the exam read: the reload first drops the
          // dashboard's pending debounced write of the old selection, which
          // would otherwise put it back over the companion's change. A lecture
          // change cannot change the primary exam or the active course.
          void Promise.allSettled([dispatch(reloadLectureSelection())]);
        }

        void (async () => {
          // A companion action may have changed the primary exam. Let that read
          // settle before either refresh reads the exam id from Redux.
          await Promise.allSettled([fetchExams()]);
          dispatch(topicFiltersRefreshRequested());

          // Reuse the app's full refresh path so same-session blocks,
          // vignettes, videos, and the overview are refreshed together.
          await Promise.allSettled([refreshStudySessionAndContent()]);
        })();
      }
    );
  }, [dispatch, executedActions, refreshStudySessionAndContent, store]);
};
